<template>
  <div
    class="fixed inset-0 z-50 overflow-y-auto"
    role="dialog"
    aria-modal="true"
    aria-labelledby="modal-title"
  >
    <!-- Background overlay -->
    <div
      class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
    >
      <div
        class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        aria-hidden="true"
        @click="$emit('close')"
      />

      <!-- This element is to trick the browser into centering the modal contents. -->
      <span
        class="hidden sm:inline-block sm:align-middle sm:h-screen"
        aria-hidden="true"
      >&#8203;</span>

      <!-- Modal panel -->
      <div
        class="relative inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6"
      >
        <!-- Header -->
        <div class="flex items-center justify-between mb-6">
          <h3
            id="modal-title"
            class="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100"
          >
            Create Template
          </h3>
          <button
            class="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            @click="$emit('close')"
          >
            <svg
              class="w-6 h-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <!-- Form -->
        <form class="space-y-4" @submit.prevent="createTemplate">
          <!-- Template Name -->
          <div>
            <label
              for="template-name"
              class="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Template Name
            </label>
            <input
              id="template-name"
              v-model="templateName"
              type="text"
              required
              class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
              placeholder="Enter template name"
            />
          </div>

          <!-- Description -->
          <div>
            <label
              for="template-description"
              class="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Description
            </label>
            <textarea
              id="template-description"
              v-model="templateDescription"
              rows="3"
              class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
              placeholder="Describe what this template does"
            />
          </div>

          <!-- Tool Selection -->
          <div>
            <label
              for="tool-select"
              class="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Tool
            </label>
            <select
              id="tool-select"
              v-model="selectedTool"
              required
              class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
            >
              <option value="">Select a tool</option>
              <option value="web_search">Web Search</option>
              <option value="calculate">Calculator</option>
              <option value="vector_store">Vector Store</option>
              <option value="telegram_bot">Telegram Bot</option>
            </select>
          </div>

          <!-- Tags -->
          <div>
            <label
              for="template-tags"
              class="block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Tags
            </label>
            <input
              id="template-tags"
              v-model="templateTags"
              type="text"
              class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-gray-100"
              placeholder="Enter tags separated by commas"
            />
            <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Separate multiple tags with commas
            </p>
          </div>

          <!-- Parameters (Dynamic based on selected tool) -->
          <div v-if="selectedTool" class="space-y-3">
            <h4 class="text-sm font-medium text-gray-700 dark:text-gray-300">Tool Parameters</h4>

            <!-- Web Search Parameters -->
            <div v-if="selectedTool === 'web_search'" class="space-y-3">
              <div>
                <label class="block text-sm text-gray-600 dark:text-gray-400">Query</label>
                <input
                  v-model="parameters.query"
                  type="text"
                  class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                  placeholder="Search query"
                />
              </div>
              <div>
                <label class="block text-sm text-gray-600 dark:text-gray-400">Count</label>
                <input
                  v-model.number="parameters.count"
                  type="number"
                  min="1"
                  max="20"
                  class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                  placeholder="Number of results"
                />
              </div>
            </div>

            <!-- Calculator Parameters -->
            <div v-if="selectedTool === 'calculate'" class="space-y-3">
              <div>
                <label class="block text-sm text-gray-600 dark:text-gray-400">Operation</label>
                <select
                  v-model="parameters.operation"
                  class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                >
                  <option value="add">Addition</option>
                  <option value="subtract">Subtraction</option>
                  <option value="multiply">Multiplication</option>
                  <option value="divide">Division</option>
                </select>
              </div>
              <div>
                <label class="block text-sm text-gray-600 dark:text-gray-400">First Number</label>
                <input
                  v-model.number="parameters.a"
                  type="number"
                  class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                />
              </div>
              <div>
                <label class="block text-sm text-gray-600 dark:text-gray-400">Second Number</label>
                <input
                  v-model.number="parameters.b"
                  type="number"
                  class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                />
              </div>
            </div>

            <!-- Vector Store Parameters -->
            <div v-if="selectedTool === 'vector_store'" class="space-y-3">
              <div>
                <label class="block text-sm text-gray-600 dark:text-gray-400">Action</label>
                <select
                  v-model="parameters.action"
                  class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                >
                  <option value="insert">Insert</option>
                  <option value="query">Query</option>
                </select>
              </div>
              <div>
                <label class="block text-sm text-gray-600 dark:text-gray-400">Content</label>
                <textarea
                  v-model="parameters.content"
                  rows="3"
                  class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                  placeholder="Content to insert or query"
                />
              </div>
            </div>

            <!-- Telegram Bot Parameters -->
            <div v-if="selectedTool === 'telegram_bot'" class="space-y-3">
              <div>
                <label class="block text-sm text-gray-600 dark:text-gray-400">Action</label>
                <select
                  v-model="parameters.action"
                  class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                >
                  <option value="send_message">Send Message</option>
                  <option value="broadcast_notification">Broadcast</option>
                  <option value="get_subscribers">Get Subscribers</option>
                </select>
              </div>
              <div v-if="parameters.action === 'send_message'">
                <label class="block text-sm text-gray-600 dark:text-gray-400">Chat ID</label>
                <input
                  v-model="parameters.chat_id"
                  type="text"
                  class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                  placeholder="Telegram chat ID"
                />
              </div>
              <div
                v-if="
                  parameters.action === 'send_message' ||
                    parameters.action === 'broadcast_notification'
                "
              >
                <label class="block text-sm text-gray-600 dark:text-gray-400">Message</label>
                <textarea
                  v-model="messageText"
                  rows="3"
                  class="mt-1 block w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 dark:bg-gray-700 dark:text-gray-100"
                  placeholder="Message content"
                />
              </div>
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="mt-6 flex items-center justify-end space-x-3">
            <button
              type="button"
              class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              @click="$emit('close')"
            >
              Cancel
            </button>
            <button
              type="submit"
              :disabled="!templateName || !selectedTool || isCreating"
              class="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span v-if="isCreating">Creating...</span>
              <span v-else>Create Template</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, watch, computed } from 'vue';

const emit = defineEmits<{
  close: [];
  created: [template: any];
}>();
// State
const templateName = ref('');
const templateDescription = ref('');
const templateTags = ref('');
const selectedTool = ref('');
const isCreating = ref(false);
const parameters = reactive<Record<string, any>>({});

// Computed properties for v-model bindings
const messageText = computed({
  get: () => parameters.text || parameters.notification_text || '',
  set: (value: string) => {
    if (parameters.action === 'send_message') {
      parameters.text = value;
    } else if (parameters.action === 'broadcast_notification') {
      parameters.notification_text = value;
    }
  },
});

// Watch for tool changes to reset parameters
watch(selectedTool, newTool => {
  // Clear existing parameters
  Object.keys(parameters).forEach(key => {
    delete parameters[key];
  });

  // Set default parameters based on tool
  switch (newTool) {
    case 'web_search':
      parameters.query = '';
      parameters.count = 10;
      break;
    case 'calculate':
      parameters.operation = 'add';
      parameters.a = 0;
      parameters.b = 0;
      break;
    case 'vector_store':
      parameters.action = 'query';
      parameters.content = '';
      break;
    case 'telegram_bot':
      parameters.action = 'send_message';
      parameters.chat_id = '';
      parameters.text = '';
      break;
  }
});

// Methods
const createTemplate = async () => {
  if (!templateName.value || !selectedTool.value) return;

  isCreating.value = true;

  try {
    // Parse tags
    const tags = templateTags.value
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0);

    // Create template object
    const template = {
      id: `template_${Date.now()}`,
      name: templateName.value,
      description: templateDescription.value,
      toolName: selectedTool.value,
      parameters: { ...parameters },
      tags,
      createdAt: new Date().toISOString(),
      usageCount: 0,
    };

    // Here you would typically save to the backend
    console.log('Creating template:', template);

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Emit success
    emit('created', template);
  } catch (error) {
    console.error('Failed to create template:', error);
    // Handle error - show notification
    if (window.showNotification) {
      window.showNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to create template',
      });
    }
  } finally {
    isCreating.value = false;
  }
};

// Handle escape key
const handleKeydown = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    emit('close');
  }
};

// Add event listener
if (typeof window !== 'undefined') {
  document.addEventListener('keydown', handleKeydown);
}

// Cleanup on unmount
import { onUnmounted } from 'vue';
onUnmounted(() => {
  if (typeof window !== 'undefined') {
    document.removeEventListener('keydown', handleKeydown);
  }
});
</script>

<style scoped>
/* Ensure modal appears above other content */
.fixed.inset-0.z-50 {
  z-index: 9999;
}

/* Smooth animations */
.transition-opacity {
  transition: opacity 150ms ease-in-out;
}

.transform.transition-all {
  transition: all 150ms ease-in-out;
}

/* Accessibility improvements */
@media (prefers-reduced-motion: reduce) {
  .transition-opacity,
  .transform.transition-all {
    transition: none;
  }
}

/* High contrast mode */
@media (prefers-contrast: high) {
  .border {
    @apply border-2;
  }
}
</style>
